import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, ProgressBar } from 'react-bootstrap';
import {
  uploadPropertyImage,
  uploadDraftPropertyImage,
  reorderPropertyImages,
  setCoverImage as apiSetCoverImage,
  deletePropertyImage as apiDeletePropertyImage,
  updateImageMeta,
} from '../services/propertyImagesApi';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 8 * 1024 * 1024;
const MIN_WIDTH = 1;
const MIN_HEIGHT = 1;
const CONCURRENCY = 3;
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const normalized = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE}${normalized}`;
};

export const getImageDimensions = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Could not read image'));
    };
    img.src = URL.createObjectURL(file);
  });

export const hashFile = async (file) => {
  if (!window?.crypto?.subtle) return null;
  const buffer = await file.arrayBuffer();
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', buffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

export const validateImageFile = async (file, seenFingerprints = new Set()) => {
  const errors = [];
  if (!ACCEPTED_TYPES.includes(file.type)) errors.push('Invalid type');
  if (file.size > MAX_SIZE) errors.push('File too large (max 8MB)');
  const dims = await getImageDimensions(file).catch(() => ({ width: 0, height: 0 }));
  if (dims.width < MIN_WIDTH || dims.height < MIN_HEIGHT) {
    errors.push(`Too small (min ${MIN_WIDTH}x${MIN_HEIGHT})`);
  }
  const fingerprint = `${file.name}-${file.size}-${file.lastModified}`;
  if (seenFingerprints.has(fingerprint)) errors.push('Duplicate selection');
  return { valid: errors.length === 0, errors, fingerprint, dims };
};

export const uploadQueue = async (items, concurrency, worker) => {
  let idx = 0;
  const workers = Array.from({ length: Math.min(concurrency, items.length) }).map(async () => {
    while (idx < items.length) {
      const current = items[idx];
      idx += 1;
      try {
        // eslint-disable-next-line no-await-in-loop
        await worker(current);
      } catch (_) {
        // worker handles its own errors to keep queue moving
      }
    }
  });
  await Promise.all(workers);
};

const normalizeSortOrder = (list = []) =>
  (list || []).map((img, idx) => ({
    ...img,
    sort_order: idx + 1,
  }));

const moveItem = (list, from, to) => {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
};

const GalleryUploader = ({ propertyId, token, images = [], onImagesChange, onError }) => {
  const [uploadItems, setUploadItems] = useState([]);
  const [queueActive, setQueueActive] = useState(false);
  const [selectionFeedback, setSelectionFeedback] = useState(null);
  const [lastError, setLastError] = useState('');
  const inputRef = useRef(null);
  const dragIndex = useRef(null);
  const imagesRef = useRef(images);

  useEffect(() => {
    imagesRef.current = images || [];
  }, [images]);

  const updateImages = useCallback(
    (next) => {
      if (!onImagesChange) return;
      const resolved = typeof next === 'function' ? next(imagesRef.current || []) : next;
      imagesRef.current = Array.isArray(resolved) ? resolved : [];
      onImagesChange(imagesRef.current);
    },
    [onImagesChange]
  );

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;
      const seen = new Set(
        uploadItems.map((u) => u.fingerprint).filter(Boolean)
      );
      const accepted = [];
      const rejected = {};

      for (const file of files) {
        // eslint-disable-next-line no-await-in-loop
        const result = await validateImageFile(file, seen);
        if (!result.valid) {
          result.errors.forEach((reason) => {
            rejected[reason] = (rejected[reason] || 0) + 1;
          });
          continue;
        }
        const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        accepted.push({
          id,
          file,
          status: 'queued',
          progress: 0,
          attempts: 0,
          error: '',
          fingerprint: result.fingerprint,
          width: result.dims.width,
          height: result.dims.height,
        });
        seen.add(result.fingerprint);
      }

      if (accepted.length) {
        setUploadItems((prev) => [...prev, ...accepted]);
      }
      setSelectionFeedback({
        accepted: accepted.length,
        rejected,
      });
      setLastError('');
    },
    [propertyId, uploadItems, onError]
  );

  const startUpload = useCallback(
    async (item) => {
      setUploadItems((prev) =>
        prev.map((u) =>
          u.id === item.id
            ? { ...u, status: 'uploading', progress: u.progress || 1, attempts: (u.attempts || 0) + 1, error: '' }
            : u
        )
      );
      try {
        const fileHash = await hashFile(item.file).catch(() => null);
        const uploadOptions = {
          onProgress: (pct) =>
            setUploadItems((prev) =>
              prev.map((u) => (u.id === item.id ? { ...u, progress: pct } : u))
            ),
          fileHash,
          width: item.width,
          height: item.height,
        };
        const uploaded = propertyId
          ? await uploadPropertyImage(propertyId, item.file, token, uploadOptions)
          : await uploadDraftPropertyImage(item.file, token, uploadOptions);
        const resolvedId = uploaded?.id ?? `draft-${item.id}`;
        const resolvedUrl = uploaded?.url || uploaded?.image_url || uploaded?.path || '';
        const newImage = {
          id: resolvedId,
          url: resolvedUrl,
          alt: uploaded.alt_text || '',
          alt_text: uploaded.alt_text || '',
          is_cover: uploaded.is_cover || imagesRef.current.length === 0,
          sort_order: uploaded.sort_order ?? imagesRef.current.length + 1,
          width: uploaded.width,
          height: uploaded.height,
          file_hash: uploaded.file_hash || fileHash || null,
        };
        updateImages((prev) => {
          const next = prev.filter((img) => {
            if (img.id === resolvedId) return false;
            if (resolvedUrl && (img.url || img.image_url) === resolvedUrl) return false;
            if (newImage.file_hash && img.file_hash === newImage.file_hash) return false;
            return true;
          });
          return normalizeSortOrder([...next, newImage]);
        });
        setUploadItems((prev) =>
          prev.map((u) => (u.id === item.id ? { ...u, status: 'done', progress: 100 } : u))
        );
      } catch (err) {
        const message = err?.message || 'Upload failed';
        setUploadItems((prev) =>
          prev.map((u) => (u.id === item.id ? { ...u, status: 'error', error: message } : u))
        );
        setLastError(message);
        onError?.(message);
      }
    },
    [propertyId, token, onError, updateImages]
  );

  useEffect(() => {
    if (queueActive) return;
    const queued = uploadItems.filter((u) => u.status === 'queued');
    if (!queued.length) return;
    setQueueActive(true);
    uploadQueue(queued, CONCURRENCY, startUpload).finally(() => setQueueActive(false));
  }, [uploadItems, queueActive, startUpload]);

  const overallProgress = useMemo(() => {
    if (!uploadItems.length) return 0;
    const total = uploadItems.reduce((acc, item) => acc + (item.status === 'done' ? 100 : item.progress || 0), 0);
    return Math.round(total / uploadItems.length);
  }, [uploadItems]);

  useEffect(() => {
    const onPaste = (e) => {
      const files = e.clipboardData?.files;
      if (files?.length) {
        handleFiles(files);
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [handleFiles]);

  const handleReorder = async (from, to) => {
    if (from === to) return;
    const reordered = normalizeSortOrder(moveItem(imagesRef.current, from, to));
    updateImages(reordered);
    try {
      const ids = reordered.map((img) => img.id).filter(Boolean);
      if (propertyId && ids.length) {
        await reorderPropertyImages(propertyId, ids, token);
      }
    } catch (err) {
      const message = err?.message || 'Failed to reorder images';
      setLastError(message);
      onError?.(message);
      updateImages(imagesRef.current); // revert
    }
  };

  const handleSetCover = async (imageId) => {
    updateImages((prev) =>
      prev.map((img) => ({
        ...img,
        is_cover: img.id === imageId,
      }))
    );
    try {
      if (propertyId) {
        await apiSetCoverImage(propertyId, imageId, token);
      }
    } catch (err) {
      const message = err?.message || 'Failed to set cover image';
      setLastError(message);
      onError?.(message);
    }
  };

  const handleDelete = async (imageId) => {
    const prevImages = imagesRef.current;
    updateImages(prevImages.filter((img) => img.id !== imageId));
    try {
      if (propertyId) {
        await apiDeletePropertyImage(propertyId, imageId, token);
      }
    } catch (err) {
      const message = err?.message || 'Failed to delete image';
      setLastError(message);
      onError?.(message);
      updateImages(prevImages); // rollback on failure
    }
  };

  const handleAltBlur = async (imageId, altText) => {
    if (!imageId) return;
    try {
      await updateImageMeta(imageId, { alt_text: altText }, token);
    } catch (_) {
      // inline failures are non-blocking; error already reflected via save
    }
  };

  const uploadingCount = uploadItems.filter((u) => u.status === 'uploading').length;
  const failedCount = uploadItems.filter((u) => u.status === 'error').length;
  const completedCount = uploadItems.filter((u) => u.status === 'done').length;
  const totalUploads = uploadItems.length;

  return (
    <div className="border rounded p-3">
      <div
        className={`border-2 border-dashed rounded p-3 mb-3 ${queueActive ? 'bg-light' : 'bg-white'}`}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
        role="presentation"
        onClick={() => inputRef.current?.click()}
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <div className="fw-semibold">Upload gallery images</div>
            <div className="text-muted small">
              Drag & drop, paste, or click to select. Types: JPEG/PNG/WebP. Max 8MB. Min {MIN_WIDTH}x{MIN_HEIGHT}.
            </div>
            {(queueActive || uploadingCount) && totalUploads > 0 && (
              <div className="small text-primary mt-1">
                Uploading {Math.min(completedCount + uploadingCount, totalUploads)}/{totalUploads}... Overall {overallProgress}%
              </div>
            )}
            {selectionFeedback && (
              <div className="small text-muted mt-1">
                Accepted {selectionFeedback.accepted}.{' '}
                {Object.keys(selectionFeedback.rejected || {}).length
                  ? `Rejected ${Object.values(selectionFeedback.rejected).reduce((a, b) => a + b, 0)}`
                  : 'No rejections.'}
              </div>
            )}
            {failedCount > 0 && (
              <div className="small text-danger mt-1">
                {failedCount} failed â€” click retry on each file.
              </div>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <Button variant="outline-secondary" size="sm" onClick={() => inputRef.current?.click()}>
              Select images
            </Button>
          </div>
        </div>
        <input
          type="file"
          accept={ACCEPTED_TYPES.join(',')}
          multiple
          ref={inputRef}
          className="d-none"
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {uploadItems.length > 0 && (
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-semibold">Upload queue</div>
            {overallProgress > 0 && <span className="small text-muted">Overall {overallProgress}%</span>}
          </div>
          {uploadItems.map((item) => (
            <div key={item.id} className="d-flex align-items-center gap-2 mb-2">
              <div className="flex-grow-1">
                <div className="d-flex justify-content-between small">
                  <span className="text-truncate">{item.file.name}</span>
                  <span className="text-muted">{formatBytes(item.file.size)}</span>
                </div>
                <ProgressBar
                  now={item.status === 'done' ? 100 : item.progress || 0}
                  variant={item.status === 'error' ? 'danger' : 'primary'}
                  striped={item.status === 'uploading'}
                  animated={item.status === 'uploading'}
                />
                <div className="small text-muted mt-1">
                  {item.status === 'uploading' && `Uploading... ${item.progress || 0}%`}
                  {item.status === 'queued' && 'Queued'}
                  {item.status === 'done' && 'Uploaded'}
                  {item.status === 'error' && <span className="text-danger">{item.error}</span>}
                </div>
              </div>
              {item.status === 'error' && (
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() =>
                    setUploadItems((prev) =>
                      prev.map((u) => (u.id === item.id ? { ...u, status: 'queued', error: '', progress: 0 } : u))
                    )
                  }
                >
                  Retry
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {lastError && (
        <Alert variant="danger" className="py-2">
          {lastError}
        </Alert>
      )}

      <div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">Gallery</h6>
          <small className="text-muted">Drag to reorder. Set cover from existing images.</small>
        </div>
        {(!images || !images.length) && (
          <div className="text-muted small border rounded p-3">No images yet. Upload to populate the gallery.</div>
        )}
        {images?.length > 0 && (
          <div className="d-flex flex-wrap gap-3">
            {images.map((img, idx) => (
              <div
                key={img.id || idx}
                className="border rounded p-2"
                style={{ width: 170 }}
                draggable
                onDragStart={() => {
                  dragIndex.current = idx;
                }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragIndex.current !== null && dragIndex.current !== idx) {
                    handleReorder(dragIndex.current, idx);
                  }
                  dragIndex.current = null;
                }}
              >
                <div className="bg-light rounded overflow-hidden mb-2" style={{ height: 110 }}>
                  <img
                    src={resolveImageUrl(img.url || img.image_url)}
                    alt={img.alt || img.alt_text || ''}
                    className="w-100 h-100"
                    style={{ objectFit: 'cover' }}
                  />
                </div>
                <Form.Control
                  size="sm"
                  placeholder="Alt text"
                  value={img.alt || img.alt_text || ''}
                  onChange={(e) =>
                    updateImages((prev) => {
                      const next = [...prev];
                      next[idx] = { ...next[idx], alt: e.target.value, alt_text: e.target.value };
                      return next;
                    })
                  }
                  onBlur={(e) => handleAltBlur(img.id, e.target.value)}
                  className="mb-2"
                />
                <div className="d-flex justify-content-between align-items-center">
                  <Button
                    size="sm"
                    variant={img.is_cover ? 'success' : 'outline-secondary'}
                    onClick={() => handleSetCover(img.id)}
                    disabled={img.is_cover}
                  >
                    {img.is_cover ? 'Cover' : 'Set cover'}
                  </Button>
                  <Button size="sm" variant="outline-danger" onClick={() => handleDelete(img.id)}>
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryUploader;


